import React, { useEffect, useState } from "react";
import axios from "axios";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import toast from "react-hot-toast";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";

const API = process.env.REACT_APP_API_URL;

const HostelBookingHistory = () => {
  const [bookings, setBookings] = useState([]);
  const [filters, setFilters] = useState({ status: "", room: "", student: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState(null);
  const role = localStorage.getItem("role");

  useEffect(() => {
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBookings = async () => {
    setIsLoading(true);
    const loadingToast = toast.loading("Loading bookings...");
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();

      if (filters.status) params.append("status", filters.status);
      if (filters.room) params.append("bed__room__room_number", filters.room);
      if (filters.student) params.append("search", filters.student);

      const res = await axios.get(
        `${API}hostel/bookings/?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.dismiss(loadingToast);
      setBookings(res.data);
      setIsLoading(false);
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error("❌ Failed to fetch hostel bookings.");
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleCancel = async (bookingId) => {
    const booking = bookings.find((b) => b.id === bookingId);
    setBookingToCancel(booking);
    setCancelDialogOpen(true);
  };

  const confirmCancel = async () => {
    if (!bookingToCancel) return;

    const loadingToast = toast.loading("Cancelling booking...");
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}hostel/bookings/${bookingToCancel.id}/cancel/`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.dismiss(loadingToast);
      toast.success("✅ Booking cancelled successfully.");
      setCancelDialogOpen(false);
      setBookingToCancel(null);
      fetchBookings();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error("❌ Failed to cancel booking.");
      setCancelDialogOpen(false);
      setBookingToCancel(null);
    }
  };

  const handleCancelDialogClose = () => {
    setCancelDialogOpen(false);
    setBookingToCancel(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800 border-green-200";
      case "pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "rejected":
        return "bg-red-100 text-red-800 border-red-200";
      case "expired":
      case "cancelled":
        return "bg-gray-100 text-gray-600 border-gray-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const formatDateTime = (iso) => {
    const date = new Date(iso);
    const options = {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    };
    const time = date.toLocaleTimeString("en-IN", options);
    const formattedDate = date.toISOString().split("T")[0];
    return `${formattedDate} ${" / "} ${time}`;
  };

  const BookingCard = ({ booking }) => (
    <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200 hover:shadow-lg transition-shadow duration-200">
      <div className="space-y-3">
        {/* Header with Status */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ApartmentOutlinedIcon className="text-blue-600 text-lg" />
            <span className="font-semibold text-gray-900">
              Room {booking.bed?.room?.room_number || "—"}
            </span>
          </div>
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
              booking.status
            )}`}
          >
            {booking.status?.toUpperCase()}
          </span>
        </div>

        {/* Admin Info */}
        {role === "admin" && (
          <div className="space-y-2 p-3 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-gray-500">Booking ID:</span>
                <span className="font-mono text-gray-900 ml-1">
                  {booking.id}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Student ID:</span>
                <span className="font-mono text-gray-900 ml-1">
                  {booking.student?.id || "—"}
                </span>
              </div>
            </div>
            <div>
              <span className="text-gray-500">Student:</span>
              <span className="font-medium text-gray-900 ml-1">
                {booking.student?.first_name && booking.student?.last_name
                  ? `${booking.student.first_name} ${booking.student.last_name}`
                  : booking.student?.first_name ||
                    booking.student?.last_name ||
                    "—"}
              </span>
            </div>
            <div className="grid grid-cols-1 gap-1 text-sm">
              <div>
                <span className="text-gray-500">Phone:</span>
                <a
                  href={`tel:${booking.student?.phone_number}`}
                  className="text-blue-600 hover:text-blue-800 underline ml-1 cursor-pointer"
                  title="Click to call"
                >
                  {booking.student?.phone_number || "—"}
                </a>
              </div>
              <div>
                <span className="text-gray-500">Address:</span>
                <span className="text-gray-900 ml-1 truncate block">
                  {booking.student?.address || "—"}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Booking Details */}
        <div className="flex flex-col gap-3 text-sm flex-wrap">
          <div>
            <span className="text-gray-500">Bed:</span>
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 ml-1">
              {booking.bed?.bed_number || "—"}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Purpose:</span>
            <span className="text-gray-900 ml-1">
              {booking?.purpose_of_joining || "—"}
            </span>
          </div>
        </div>

        {/* Dates */}
        <div className="space-y-1 text-sm">
          <div>
            <span className="text-gray-500">Booking Date:</span>
            <span className="text-gray-900 ml-1">
              {formatDateTime(booking.created_at)}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Starting Date:</span>
            <span className="text-gray-900 ml-1">{booking.start_date}</span>
          </div>
          <div>
            <span className="text-gray-500">End Date:</span>
            <span className="text-gray-900 ml-1">
              {booking.end_date || "—"}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-200">
          {(booking.status === "pending" || booking.status === "approved") && (
            <button
              onClick={() => handleCancel(booking.id)}
              className="bg-red-600 text-white px-3 py-2 rounded-lg text-xs hover:bg-red-700 transition-colors duration-200 shadow-sm flex-1"
            >
              Cancel
            </button>
          )}
          {/* {role === "admin" && (
            <button
              onClick={() => handleDownloadInvoice(booking.id)}
              className="bg-gray-800 text-white px-3 py-2 rounded-lg text-xs hover:bg-gray-900 transition-colors flex-1 duration-200 shadow-sm flex items-center justify-center gap-1"
            >
              📄 Invoices
            </button>
          )} */}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 md:p-4">
      <div className="max-w-full mx-auto">
        {/* Filters */}
        {role === "admin" && (
          <div className="bg-white rounded-xl shadow-lg p-4 md:p-3 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <FilterListIcon className="text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-800">Filters</h3>
              </div>
              <button
                onClick={fetchBookings}
                disabled={isLoading}
                className="flex items-center gap-2 bg-gray-100 text-gray-600 p-2 rounded-lg hover:bg-gray-200 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Refresh bookings"
              >
                <RefreshIcon
                  className={`text-sm ${isLoading ? "animate-spin" : ""}`}
                />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
              <div className="relative">
                <select
                  name="status"
                  onChange={handleChange}
                  value={filters.status}
                  className="w-full border border-gray-300 px-3 py-2 md:px-4 md:py-3 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm"
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div className="relative">
                <input
                  name="room"
                  placeholder="Room Number"
                  value={filters.room}
                  onChange={handleChange}
                  className="w-full border border-gray-300 px-3 py-2 md:px-4 md:py-3 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm"
                />
              </div>
              <div className="relative">
                <input
                  name="student"
                  placeholder="Student Name"
                  value={filters.student}
                  onChange={handleChange}
                  className="w-full border border-gray-300 px-3 py-2 md:px-4 md:py-3 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm"
                />
              </div>
              <button
                onClick={fetchBookings}
                className="flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2 md:px-6 md:py-3 rounded-lg hover:bg-blue-700 transition-colors duration-200 shadow-md text-sm"
              >
                <SearchIcon className="text-sm" />
                <span className="hidden sm:inline">Apply Filters</span>
                <span className="sm:hidden">Filter</span>
              </button>
            </div>
          </div>
        )}

        {/* Non-admin refresh button */}
        {role !== "admin" && (
          <div className="mb-4">
            <button
              onClick={fetchBookings}
              disabled={isLoading}
              className="flex items-center gap-2 bg-white text-gray-600 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
              title="Refresh bookings"
            >
              <RefreshIcon
                className={`text-sm ${isLoading ? "animate-spin" : ""}`}
              />
              <span className="text-sm">Refresh</span>
            </button>
          </div>
        )}

        {/* Mobile Cards View */}
        <div className="block md:hidden">
          {bookings.length === 0 ? (
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <div className="flex flex-col items-center gap-2">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                  <ApartmentOutlinedIcon className="text-gray-400 text-2xl" />
                </div>
                <p className="text-gray-500 font-medium">No bookings found</p>
                <p className="text-gray-400 text-sm">
                  Try adjusting your filters or refresh the page
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {bookings.map((booking, index) => (
                <BookingCard key={index} booking={booking} />
              ))}
            </div>
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden md:block bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto max-h-[calc(100vh-105px)] overflow-y-auto">
            <table className="min-w-full">
              <thead className="bg-gradient-to-r from-blue-600 to-blue-700 text-white sticky top-0 z-10">
                <tr>
                  {role === "admin" && (
                    <>
                      <th className="px-4 py-4 text-left text-sm font-semibold sticky left-0 z-10 bg-gradient-to-r from-blue-600 to-blue-700">
                        Booking ID
                      </th>
                      <th className="px-4 py-4 text-left text-sm font-semibold sticky left-20 z-10 bg-gradient-to-r from-blue-600 to-blue-700">
                        Student ID
                      </th>
                      <th className="px-4 py-4 text-left text-sm font-semibold sticky left-32 z-10 bg-gradient-to-r from-blue-600 to-blue-700">
                        Student Name
                      </th>
                      <th className="px-4 py-4 text-left text-sm font-semibold">
                        Phone
                      </th>
                      <th className="px-4 py-4 text-left text-sm font-semibold">
                        Address
                      </th>
                    </>
                  )}
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Room
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Bed
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Purpose
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Booking Date
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Starting Date
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    End Date
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Status
                  </th>
                  <th className="px-4 py-4 text-left text-sm font-semibold">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {bookings.length === 0 ? (
                  <tr>
                    <td
                      colSpan={role === "admin" ? "12" : "7"}
                      className="px-4 py-8 text-center"
                    >
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                          <ApartmentOutlinedIcon className="text-gray-400 text-2xl" />
                        </div>
                        <p className="text-gray-500 font-medium">
                          No bookings found
                        </p>
                        <p className="text-gray-400 text-sm">
                          Try adjusting your filters or refresh the page
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  bookings.map((b, i) => (
                    <tr
                      key={i}
                      className="hover:bg-gray-50 transition-colors duration-200"
                    >
                      {role === "admin" && (
                        <>
                          <td className="px-4 py-4 font-mono text-gray-600 sticky left-0 bg-white hover:bg-gray-50">
                            {b.id}
                          </td>
                          <td className="px-4 py-4 font-mono text-gray-600 sticky left-20 bg-white hover:bg-gray-50">
                            {b.student?.id || "—"}
                          </td>
                          <td className="px-4 py-4 sticky left-32 bg-white hover:bg-gray-50">
                            <div className="font-medium text-gray-900">
                              {b.student?.first_name && b.student?.last_name
                                ? `${b.student.first_name} ${b.student.last_name}`
                                : b.student?.first_name ||
                                  b.student?.last_name ||
                                  "—"}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-gray-600">
                            <a
                              href={`tel:${b.student?.phone_number}`}
                              className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                              title="Click to call"
                            >
                              {b.student?.phone_number || "—"}
                            </a>
                          </td>
                          <td className="px-4 py-4 text-gray-600 max-w-xs truncate">
                            {b.student?.address || "—"}
                          </td>
                        </>
                      )}
                      <td className="px-4 py-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-md font-medium bg-blue-100 text-blue-800">
                          {b.bed?.room?.room_number || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-md font-medium bg-green-100 text-green-800">
                          {b.bed?.bed_number || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-gray-600 max-w-xs truncate">
                        {b?.purpose_of_joining || "—"}
                      </td>
                      <td className="px-4 py-4 text-gray-600 text-sm">
                        {formatDateTime(b.created_at)}
                      </td>
                      <td className="px-4 py-4 text-gray-600">
                        {b.start_date}
                      </td>
                      <td className="px-4 py-4 text-gray-600">
                        {b.end_date || "—"}
                      </td>
                      <td className="px-4 py-4">
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                            b.status
                          )}`}
                        >
                          {b.status?.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col gap-2">
                          {(b.status === "pending" ||
                            b.status === "approved") && (
                            <button
                              onClick={() => handleCancel(b.id)}
                              className="bg-red-600 text-white px-3 py-2 rounded-lg text-xs hover:bg-red-700 transition-colors duration-200 shadow-sm"
                            >
                              Cancel
                            </button>
                          )}
                          {/* {role === "admin" && (
                            <button
                              onClick={() => handleDownloadInvoice(b.id)}
                              className="bg-gray-800 w-max text-white px-3 py-2 rounded-lg text-xs hover:bg-gray-900 transition-colors duration-200 shadow-sm flex items-center gap-1"
                            >
                              📄 Invoices
                            </button>
                          )} */}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={handleCancelDialogClose}
        aria-labelledby="cancel-dialog-title"
        aria-describedby="cancel-dialog-description"
      >
        <DialogTitle id="cancel-dialog-title">
          <WarningIcon className="text-red-500 mr-2" />
          Confirm Cancellation
        </DialogTitle>
        <DialogContent>
          <Typography id="cancel-dialog-description">
            Are you sure you want to cancel this booking? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDialogClose} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmCancel} color="error" variant="contained">
            Confirm Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default HostelBookingHistory;
